<template>
    <article class="forum-post-result">
        <ForumAvatar :src="getAvatarUrl(post)">
        </ForumAvatar>

        <div class="post-body">

            <div class="post-meta">
                <div class="post-context">
                    <router-link
                        class="author-name"
                        :to="getAuthorUrl(post)">
                        {{ getAuthorName(post) }}
                    </router-link>
                    <span
                        class="author-role"
                        v-if="getAuthorRole(post)">
                        <img
                            :src="getAuthorRoleIcon(post)"
                            :title="getAuthorRole(post)">
                    </span>
                    <span>in</span>
                    <router-link
                        class="thread-title"
                        :to="threadUrl">
                        {{ thread.title }}
                    </router-link>
                </div>
                <div class="post-date">
                    <span
                        class="edit-status"
                        :title="editStatus(post)"
                        v-if="hasBeenEdited(post)">
                        <i class="fa fa-pencil"></i>
                    </span>
                    <span :title="postStatus(post)">
                        {{ prettyPostDate(post) }}
                    </span>
                </div>
            </div>

            <div class="post-content">
                <!-- This got abused a little bit for user in the ForumUser
                     component... Probably shouldn't be a customizable slot
                     here. -->
                <slot name="post-content">
                    <p
                        v-for="excerpt of post.excerpts"
                        v-html="excerpt">
                    </p>
                </slot>
            </div>

            <div class="post-actions">
                <router-link :to="postUrl">
                    Go to post
                    <i class="fa fa-arrow-right"></i>
                </router-link>
            </div>
        </div>
    </article>
</template>

<script>
    // ForumPostResult: an alternative forum post component that provides more
    // context than the regular forum post, such as the thread title. Currently
    // used by forum search and forum user.

    import ForumUtils from 'src/utils/forum';
    import UserUtils from 'src/utils/users';
    import ForumAvatar from './avatar';

    export default {
        props: {
            /**
             * @param {object}   [author]
             * @param {string}   author.[name]
             * @param {string[]} author.[roles]
             * @param {string[]} author.[termIds]
             * @param {string}   author.[avatarUrl]
             * @param {Date}     [createdAt]
             * @param {Date}     [updatedAt]
             * @param {string[]} excerpts - plain text with <em> tags
             */
            post: {
                type: Object,
                required: true,
            },

            /**
             * @param {number} id
             * @param {string} title
             */
            thread: {
                type: Object,
                required: true,
            },
        },

        components: {
            ForumAvatar,
        },

        methods: {
            // TODO: replace getAuthorBlank(post) style of methods with
            // getAuthorBlank(post.author) style to match other forum components

            getAuthorName(post) {
                if (post && post.author && post.author.name) {
                    return post.author.name;
                } else {
                    return 'Anonymous';
                }
            },

            getAuthorRole(post) {
                if (post &&
                    post.author &&
                    post.author.roles &&
                    post.author.roles.length > 0) {

                    return UserUtils.getDisplayRole({
                        roles: post.author.roles,
                        currentTerm: post.author.currentTerm,
                    });
                } else {
                    return '';
                }
            },

            getAuthorRoleIcon(post) {
                if (post &&
                    post.author &&
                    post.author.roles &&
                    post.author.roles.length > 0) {

                    return UserUtils.getDisplayRoleIcon({
                        roles: post.author.roles,
                        currentTerm: post.author.currentTerm,
                    });
                } else {
                    return '';
                }
            },

            getAuthorUrl(post) {
                if (post && post.author && post.author.id) {
                    return {
                        name: 'forum-user',
                        params: {
                            userId: post.author.id,
                        },
                    };
                } else {
                    return '';
                }
            },

            getAvatarUrl(post) {
                if (post && post.author && post.author.avatarUrl) {
                    return post.author.avatarUrl;
                } else {
                    return null;
                }
            },

            hasBeenEdited(post) {
                return (post &&
                        post.createdAt &&
                        post.updatedAt &&
                        post.createdAt.getTime() !== post.updatedAt.getTime());
            },

            editStatus(post) {
                if (post && post.updatedAt) {
                    const d = ForumUtils.formatFullDate(post.updatedAt);
                    return `Edited on ${d}`;
                } else {
                    return 'Never edited';
                }
            },

            postStatus(post) {
                if (post && post.createdAt) {
                    const d = ForumUtils.formatFullDate(post.createdAt);
                    return `Posted on ${d}`;
                } else {
                    return 'Not posted yet';
                }
            },

            prettyPostDate(post) {
                if (post && post.createdAt) {
                    return ForumUtils.formatRecentDate(post.createdAt);
                } else {
                    return 'Not posted yet';
                }
            },
        },

        computed: {
            threadUrl() {
                const slug = ForumUtils.formatTitlePreview(this.thread.title);
                return {
                    name: 'forum-thread',
                    params: {
                        threadId: this.thread.id,
                        threadTitlePreview: slug,
                    },
                };
            },

            postUrl() {
                const slug = ForumUtils.formatTitlePreview(this.thread.title);
                return {
                    name: 'forum-post',
                    params: {
                        threadId: this.thread.id,
                        threadTitlePreview: slug,
                        postId: this.post.id,
                    },
                };
            },
        },
    }
</script>

<style lang="scss" scoped>
    .forum-post-result {
        display: flex;
        background-color: #222;
        margin: 20px;
    }

    .forum-avatar {
        margin-top: 19px;
    }

    .post-body {
        margin: 17px 0 0 12px;
        flex: 1;

        .post-meta {
            display: flex;
            padding-right: 10px;

            .post-context {
                flex: 1;
                display: flex;

                .author-name {
                    color: #b0b0b0;
                    font-weight: bold;
                    margin-right: 6px;

                    &:hover {
                        color: #33ccff;
                        text-decoration: none;
                    }
                }

                .author-role {
                    opacity: 0.6;
                    margin-right: 6px;

                    img {
                        opacity: 0.8; // Even darker
                        width: 20px;
                        height: 20px;
                    }
                }

                .thread-title {
                    margin-left: 6px;
                }
            }

            .post-date {
                margin-left: 10px;

                .edit-status {
                    margin-right: 5px;
                }
            }
        }

        .post-content {
            padding-right: 10px;
            margin-top: 15px;

            p {
                line-height: 1.6;
            }
        }

        .post-actions {
            //padding-right: 10px;
            margin-top: 30px;
            text-align: right;

            a {
                background: none;
                border: none;
                color: #b0b0b0;
                padding: 8px 10px;
                //font-size: 18px;
                text-decoration: none;

                &:hover {
                    background: rgba(192, 192, 192, 0.12);
                    color: #33ccff;
                }

                i {
                    margin-left: 3px;
                }
            }
        }
    }
</style>

<style lang="scss">
    .forum-post-result {
        .post-content {
            em { // for highlights
                font-style: normal;
                font-weight: bold;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// post-result.vue?84cddc02